import { mPerLat, mPerLon } from './geo';
import { toWgs } from './dxf';

// stakeout geometry in local metres (ellipsoidal scale at the reference latitude)
export const STAKE_TOL = { survey: { h: 0.03, v: 0.02 }, plan: { h: 0.05, v: 0.05 } };
export const stakeTol = (project, profile) => ({ ...STAKE_TOL[profile === 'plan' ? 'plan' : 'survey'], ...(project?.skTol?.[profile] || {}) });

const xy = (o) => { const ky = mPerLat(o[0]), kx = mPerLon(o[0]); return (p) => [(p[1] - o[1]) * kx, (p[0] - o[0]) * ky]; };
const ll = (o) => { const ky = mPerLat(o[0]), kx = mPerLon(o[0]); return ([x, y]) => [o[0] + y / ky, o[1] + x / kx]; };

// offsets of a target relative to a heading: fwd = ahead, right = to the right (m)
export const relDir = (dN, dE, hdg) => { const r = (hdg * Math.PI) / 180; return { fwd: dN * Math.cos(r) + dE * Math.sin(r), right: -dN * Math.sin(r) + dE * Math.cos(r) }; };
export const bearingDeg = (dN, dE) => ((Math.atan2(dE, dN) * 180) / Math.PI + 360) % 360;

// position vs. polyline: chainage, signed cross offset (+ right of the line direction), distance to next vertex
export function lineInfo(pos, pts) {
  if (!pos || !pts || pts.length < 2) return null;
  const f = xy([pos.lat, pos.lon]), P = pts.map(f);
  let best = null, ch = 0;
  for (let i = 0; i < P.length - 1; i++) {
    const [ax, ay] = P[i], [bx, by] = P[i + 1], vx = bx - ax, vy = by - ay, L = Math.hypot(vx, vy);
    if (L < 1e-6) continue;
    const u = Math.max(0, Math.min(1, (-ax * vx - ay * vy) / (L * L))), qx = ax + u * vx, qy = ay + u * vy, d = Math.hypot(qx, qy);
    if (!best || d < best.d) best = { d, i, station: ch + u * L, off: (vx * -ay - vy * -ax) / L * -1, toNext: (1 - u) * L, segBrg: bearingDeg(vy, vx), qx, qy, u };
    ch += L;
  }
  if (!best) return null;
  return { ...best, length: ch, dN: best.qy, dE: best.qx };
}

// points every `step` m along the polyline, shifted `offset` m to the right (negative = left)
export function intervalPts(pts, step, offset = 0, name = 'P') {
  if (!pts || pts.length < 2 || !(step > 0)) return [];
  const o = pts[0], f = xy(o), g = ll(o), P = pts.map(f), out = [];
  let ch = 0, next = 0;
  for (let i = 0; i < P.length - 1; i++) {
    const [ax, ay] = P[i], [bx, by] = P[i + 1], vx = bx - ax, vy = by - ay, L = Math.hypot(vx, vy);
    if (L < 1e-6) continue;
    const nx = vy / L, ny = -vx / L;
    while (next <= ch + L + 1e-6 && out.length < 2000) {
      const u = (next - ch) / L, p = g([ax + u * vx + nx * offset, ay + u * vy + ny * offset]);
      out.push({ name: `${name} ${next.toFixed(2).replace('.', ',')} m${offset ? ` (${offset > 0 ? '+' : ''}${offset.toFixed(2).replace('.', ',')})` : ''}`, lat: p[0], lon: p[1], z: null });
      next += step;
    }
    ch += L;
  }
  const endCh = ch, lastCh = out.length ? next - step : -1;
  if (endCh - lastCh > 0.01 && out.length < 2000) {
    const n = P.length - 1, [ax, ay] = P[n - 1], [bx, by] = P[n], L = Math.hypot(bx - ax, by - ay) || 1, p = g([bx + ((by - ay) / L) * offset, by - ((bx - ax) / L) * offset]);
    out.push({ name: `${name} ${endCh.toFixed(2).replace('.', ',')} m${offset ? ` (${offset > 0 ? '+' : ''}${offset.toFixed(2).replace('.', ',')})` : ''}`, lat: p[0], lon: p[1], z: null });
  }
  return out;
}

// CSV point list: name;E;N;H or name,lat,lon,h (header lines and decimal commas tolerated)
export function parseCsv(text, crs = 'utm32') {
  const out = [];
  String(text).split(/\r?\n/).forEach((line, i) => {
    const c = line.includes(';') ? line.split(';') : line.includes('\t') ? line.split('\t') : line.split(',');
    if (c.length < 3) return;
    const n = c.slice(1, 4).map((v) => (v == null || v.trim() === '' ? null : +v.trim().replace(',', '.')));
    if (!Number.isFinite(n[0]) || !Number.isFinite(n[1])) return;
    const p = crs === 'wgs84' ? [n[0], n[1]] : toWgs(crs, n[0], n[1]);
    if (!p.every(Number.isFinite)) return;
    out.push({ name: c[0].trim() || `P${i + 1}`, lat: p[0], lon: p[1], z: Number.isFinite(n[2]) ? n[2] : null });
  });
  return out;
}

// summary of logged stakes for PDF
export function stakeStats(stakes) {
  const ds = stakes.map((s) => s.d).filter((v) => v != null);
  if (!ds.length) return null;
  const ok = stakes.filter((s) => s.tolOk).length, judged = stakes.filter((s) => s.tolOk != null).length;
  return { n: stakes.length, mean: ds.reduce((a, b) => a + b, 0) / ds.length, max: Math.max(...ds), ok, judged };
}
